import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import ReadOnlyInputBasic from "../../../../components/input/ReadOnlyInputBasic";
import {getUser, updateUser} from "../../../../api/user/UserAccountService";
import {DetailSummaryButton} from "../../../../components/detailLayout";
import Th_title from "../../../../components/Th_title";
import {apiLoading, refresh, skeleton} from "../../../../redux/slice/apiSlice";
import "../userAccountEditForm.css";
import UserAccountFormTable from "./UserAccountFormTable";
import UserAccountProfileImageField, {UserAccountMediaRemoveButton} from "./UserAccountProfileImageField";
import SignaturePadModal from "../../../../components/modal/SignaturePadModal";
import {emailReceiveAuth, serviceReceiveAuth} from "../userAccountAuthOptions";
import {CacheType, userAuth} from "../../../../constant/CodeSpecNum";
import {
    deleteFileById,
    findUserPortraitFile,
    findUserPortraitFileId,
    findUserSignFileId,
    uploadImageSign,
} from "../../../../api/file/FileService";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import JellyCheckBox from "../../../../components/search/JellyCheckBox";
import {isAdmin, isEmpty, isSystem} from "../../../../components/commonmethod/CommonMethod";
import {onCreateClickListener} from "../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../components/selector/Selector";
import {inputOnChange} from "../../../../components/commonmethod/simmsInputMethod";
import SimmsInput from "../../../../components/input/SimmsInput";
import {SimmsRadio} from "../../../../components/input/SimmsRadio";
import {dateOnChange} from "../../../../components/commonmethod/simmsDateMethod";
import SimmsDatePicker from "../../../../components/datepicker/SimmsDatePicker";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../components/drawer/simms/SimmsDrawerSearch";

function EditSection({title, children}) {
    return (
        <section className="user-account-edit-form__section">
            <h3 className="user-account-edit-form__section-title">{title}</h3>
            <div className="user-account-edit-form__section-body">{children}</div>
        </section>
    );
}

function buildRoles(accountRoles = []) {
    return Object.values(userAuth)
        .filter((each) => each.value !== "SYSTEM")
        .map((role) => ({
            checked: accountRoles.includes(role.value),
            text: role.kor,
            value: role.value,
        }));
}

function buildMediaUrl(base, path) {
    if (!path) {
        return undefined;
    }

    const trimmed = String(path).trim();
    if (!trimmed) {
        return undefined;
    }

    if (/^(https?:)?\/\//i.test(trimmed) || trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
        return trimmed;
    }

    const baseTrimmed = (base ?? "").replace(/\/$/, "");
    const pathPart = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${baseTrimmed}${pathPart}`;
}

const UserAccountEditForm = forwardRef(function UserAccountEditForm(
    {userId, initialData, embedded = false, hideActions = false, onCancel, onSuccess},
    ref,
) {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const IMG_BASE = import.meta.env.VITE_IMG_BASE;
    const viewer = useSelector((state) => state.auth.user);
    const fileInputRef = useRef(null);
    const signBlobRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const refreshCache = useCacheRefresh();

    const [showSignModal, setShowSignModal] = useState(false);
    const [userAccount, setUserAccount] = useState({});
    const [roles, setRoles] = useState([]);
    const [btnRollup, setBtnRollup] = useState(false);
    const [pendingProfileFile, setPendingProfileFile] = useState(null);
    const [pendingProfilePreview, setPendingProfilePreview] = useState(null);
    const [pendingProfileFileName, setPendingProfileFileName] = useState(null);
    const [pendingSignPreview, setPendingSignPreview] = useState(null);
    const [profileRemoved, setProfileRemoved] = useState(false);
    const [signRemoved, setSignRemoved] = useState(false);
    const [profileFileId, setProfileFileId] = useState(null);
    const [profileOriginalName, setProfileOriginalName] = useState("");
    const [signFileId, setSignFileId] = useState(null);

    const profileDisplayName = profileRemoved
        ? (pendingProfileFileName ?? "")
        : (pendingProfileFileName ?? profileOriginalName ?? "");
    const profilePreviewSrc = profileRemoved
        ? pendingProfilePreview
        : (pendingProfilePreview
            ?? (userAccount.profileImage ? buildMediaUrl(IMG_BASE, userAccount.profileImage) : null));
    const signPreviewSrc = signRemoved
        ? pendingSignPreview
        : (pendingSignPreview
            ?? (userAccount.signatureImage ? buildMediaUrl(IMG_BASE, userAccount.signatureImage) : null));
    const canRemoveProfile = !profileRemoved && Boolean(profileDisplayName || userAccount.profileImage || pendingProfilePreview);
    const hasSignature = !signRemoved && Boolean(userAccount.signatureImage || pendingSignPreview);

    const loadMediaFileIds = useCallback(async (id) => {
        if (!id) {
            setProfileFileId(null);
            setProfileOriginalName("");
            setSignFileId(null);
            return;
        }

        const [portraitFile, signatureId] = await Promise.all([
            findUserPortraitFile(id),
            findUserSignFileId(id),
        ]);
        setProfileFileId(portraitFile?.id ?? null);
        setProfileOriginalName(portraitFile?.originalName ?? "");
        setSignFileId(signatureId);
    }, []);

    const applyUserData = useCallback((data) => {
        setUserAccount(data);
        setRoles(buildRoles(data.roles));
        setPendingProfileFile(null);
        setPendingProfilePreview(null);
        setPendingProfileFileName(null);
        setPendingSignPreview(null);
        setProfileRemoved(false);
        setSignRemoved(false);
        signBlobRef.current = null;
        loadMediaFileIds(data.id);
    }, [loadMediaFileIds]);

    const loadUser = useCallback(() => {
        if (!userId) {
            return;
        }

        if (!embedded) {
            dispatch(refresh(false));
            dispatch(skeleton(true));
        }

        setBtnRollup(true);
        getUser(userId).then((response) => {
            if (response.status === 201) {
                if (response.data.errorCode === "AUTHORITY") {
                    alert(response.data.reason);
                    navigate(-1);
                }
            }

            if (response?.data) {
                applyUserData(response.data);
            }
        }).finally(() => {
            if (!embedded) {
                dispatch(skeleton(false));
            }
        });
    }, [applyUserData, dispatch, embedded, navigate, userId]);

    useEffect(() => {
        if (!userId) {
            return;
        }

        setUserAccount({});
        setRoles([]);

        if (embedded && initialData?.id && initialData?.auth) {
            applyUserData(initialData);
            setBtnRollup(true);
            return;
        }

        loadUser();
        // embedded 초기 데이터는 마운트 시 1회만 반영 (userId 변경·폼 remount 기준)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const removeLinkedFileIfExists = async (knownFileId, findFileId) => {
        const fileId = knownFileId ?? await findFileId(userId);
        if (!fileId) {
            return;
        }

        try {
            await deleteFileById(fileId);
        } catch {
            // 파일 레코드 삭제 실패는 무시
        }
    };

    const submit = useCallback(async () => {
        const requiredFields = ["company", "department", "name", "email"];
        if (isAdmin(viewer)) {
            requiredFields.push("userGroup");
        }

        if (requiredFields.some((field) => isEmpty(userAccount[field]))) {
            alert("필수값이 누락되었습니다.");
            return;
        }

        dispatch(apiLoading(true));

        const userRoles = roles.filter((each) => each.checked).map((each) => each.value);
        const payload = {
            ...userAccount,
            roles: userRoles,
        };

        if (!payload.password) {
            delete payload.password;
        }
        if (profileRemoved) {
            payload.profileImage = null;
        }
        if (signRemoved) {
            payload.signatureImage = null;
        }

        const imageFiles = pendingProfileFile ? [pendingProfileFile] : [];

        try {
            const response = await updateUser(payload, imageFiles);

            if (response.status === 200) {
                if (profileRemoved) {
                    await removeLinkedFileIfExists(profileFileId, findUserPortraitFileId);
                }
                if (signRemoved) {
                    await removeLinkedFileIfExists(signFileId, findUserSignFileId);
                }
                if (signBlobRef.current) {
                    await uploadImageSign(userId, userAccount.name, signBlobRef.current);
                }

                refreshCache(CacheType.userAccount);
                handleActiveToast({message: "저장되었습니다.", labelListener: () => {}});

                if (embedded) {
                    onSuccess?.(userId);
                } else {
                    onSuccess?.(userId);
                }
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data?.title, description: response.data?.reason});
            } else if (response.response?.status === 409) {
                handleActiveError({
                    message: response.response.data?.title,
                    description: response.response.data?.reason,
                });
            } else if (response.response?.status === 400) {
                alert(response.response.data);
            } else {
                handleActiveError({message: "저장에 실패했습니다."});
            }
        } finally {
            dispatch(apiLoading(false));
        }
    }, [
        dispatch,
        embedded,
        onSuccess,
        pendingProfileFile,
        profileFileId,
        profileRemoved,
        refreshCache,
        roles,
        signFileId,
        signRemoved,
        userAccount,
        userId,
        viewer,
    ]);

    useImperativeHandle(ref, () => ({submit}), [submit]);

    const handleImageUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setPendingProfileFile(file);
        setPendingProfileFileName(file.name);
        setPendingProfilePreview(URL.createObjectURL(file));
        setProfileRemoved(false);
    };

    const handleRemoveProfileImage = () => {
        setPendingProfileFile(null);
        setPendingProfilePreview(null);
        setPendingProfileFileName(null);
        setProfileRemoved(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleSignSave = (dataURL, blob) => {
        setPendingSignPreview(dataURL);
        signBlobRef.current = blob;
        setSignRemoved(false);
    };

    const handleRemoveSign = () => {
        setPendingSignPreview(null);
        signBlobRef.current = null;
        setSignRemoved(true);
    };

    const handleCheckboxChange = (event, idx) => {
        setRoles((prev) =>
            prev.map((role, index) =>
                index === idx ? {...role, checked: event.target.checked} : role,
            ),
        );
    };

    if (Object.keys(userAccount).length === 0) {
        return embedded ? (
            <div className="user-account-manage-editor__loading">불러오는 중…</div>
        ) : null;
    }

    const formContent = (
        <div className={`user-account-edit-form${embedded ? " user-account-edit-form--embedded" : ""}`}>
            <EditSection title="사용자 정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="업체" colspan={1} className="fieldset-td" required auth={userAccount.auth?.update}>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "company", userAccount.company, v)}
                                    data={userAccount.company}
                                    entity="company"
                                    uniqueKey="company"
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="업체명" />
                                </Selector>
                            </Th_title>
                            <Th_title title="부서" colspan={1} className="fieldset-td" required auth={userAccount.auth?.update}>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "department", userAccount.department, v)}
                                    data={userAccount.department}
                                    entity="department"
                                    uniqueKey="department"
                                    recursive
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="사용자 그룹" colspan={3} className="fieldset-td" required auth={isAdmin(viewer)}>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "userGroup", userAccount.userGroup, v)}
                                    data={userAccount.userGroup}
                                    entity="userGroup"
                                    uniqueKey="userGroup"
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="그룹명" />
                                </Selector>
                            </Th_title>
                        </tr>
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="기본정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="이름" colspan={1} className="fieldset-td" required auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "name", v, true)}
                                    data={userAccount.name}
                                />
                            </Th_title>
                            <Th_title title="아이디" colspan={1} className="fieldset-td">
                                <ReadOnlyInputBasic target="username" value={userAccount.username} />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="직급" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "position", v, false)}
                                    data={userAccount.position}
                                />
                            </Th_title>
                            <Th_title title="사번" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "employeeNumber", v, false)}
                                    data={userAccount.employeeNumber}
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="비밀번호" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "password", v, false)}
                                    placeHolder="변경 시에만 입력"
                                    constraint={{type: "password"}}
                                />
                            </Th_title>
                            <Th_title title="이메일" colspan={1} className="fieldset-td" required auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "email", v, true)}
                                    data={userAccount.email}
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="핸드폰번호" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "phoneNumber", v, false)}
                                    data={userAccount.phoneNumber}
                                />
                            </Th_title>
                            <Th_title title="사무실번호" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsInput
                                    onChange={(v) => inputOnChange(setUserAccount, "officeNumber", v, false)}
                                    data={userAccount.officeNumber}
                                />
                            </Th_title>
                        </tr>
                        <tr>
                            <Th_title title="프로필 이미지" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <UserAccountProfileImageField
                                    fileInputRef={fileInputRef}
                                    fileName={profileDisplayName}
                                    showRemove={canRemoveProfile}
                                    previewSrc={profilePreviewSrc}
                                    onFileChange={handleImageUpload}
                                    onRemove={handleRemoveProfileImage}
                                />
                            </Th_title>
                            <Th_title title="서명등록" colspan={1} className="fieldset-td" auth={userAccount.auth?.update}>
                                <div className="user-account-edit-form__media">
                                    <button
                                        type="button"
                                        className="user-account-edit-form__media-btn"
                                        onClick={() => setShowSignModal(true)}
                                    >
                                        입력
                                    </button>
                                    {hasSignature && (
                                        <>
                                            <UserAccountMediaRemoveButton onClick={handleRemoveSign} />
                                            {signPreviewSrc && (
                                                <img src={signPreviewSrc} className="user-account-edit-form__sign" alt="" />
                                            )}
                                        </>
                                    )}
                                </div>
                            </Th_title>
                        </tr>
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="권한정보">
                <UserAccountFormTable>
                    <tbody>
                        <tr>
                            <Th_title title="이메일 수신" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                <SimmsRadio
                                    options={emailReceiveAuth}
                                    onChange={(v) => inputOnChange(setUserAccount, "emailReceiveAuthority", v, true)}
                                    data={userAccount.emailReceiveAuthority}
                                />
                            </Th_title>
                        </tr>
                        {!isAdmin(viewer) && (
                            <>
                                {platFrom !== "IAM" && (
                                    <tr>
                                        <Th_title title="서비스접수 권한" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                            <ReadOnlyInputBasic
                                                target="serviceReceiveAuthority"
                                                value={userAccount.serviceReceiveAuthority ? "활성화" : "비활성화"}
                                            />
                                        </Th_title>
                                    </tr>
                                )}
                                <tr>
                                    <Th_title title="사용자 권한" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                        <span>
                                            {userAccount.roles.map((key) => userAuth[key]?.kor || key).join(", ")}
                                        </span>
                                    </Th_title>
                                </tr>
                            </>
                        )}
                        {isAdmin(viewer) && (
                            <>
                                {platFrom !== "IAM" && (
                                    <tr>
                                        <Th_title title="서비스접수 권한" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                            <SimmsRadio
                                                options={serviceReceiveAuth}
                                                onChange={(v) => inputOnChange(setUserAccount, "serviceReceiveAuthority", v, true)}
                                                data={userAccount.serviceReceiveAuthority}
                                            />
                                        </Th_title>
                                    </tr>
                                )}
                                <tr>
                                    <Th_title title="사용자 권한" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                        <div className="user-account-edit-form__roles">
                                            {roles.map((each, idx) => (
                                                <JellyCheckBox
                                                    key={each.value}
                                                    title={each.text}
                                                    checked={each.checked}
                                                    fnc={(e) => handleCheckboxChange(e, idx)}
                                                />
                                            ))}
                                        </div>
                                    </Th_title>
                                </tr>
                            </>
                        )}
                        <tr>
                            <Th_title title="대결자" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                <Selector
                                    onChange={(v) => onCreateClickListener(setUserAccount, "proxy", userAccount.proxy, v)}
                                    data={userAccount.proxy}
                                    entity="userAccount"
                                    uniqueKey="proxy"
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                    <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                                    <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                                </Selector>
                            </Th_title>
                        </tr>
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            <EditSection title="사용기간">
                <UserAccountFormTable>
                    <tbody>
                        {!isSystem(viewer) && (
                            <tr>
                                <Th_title title="사용기간(단위: 일)" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                    <ReadOnlyInputBasic target="usePeriodDate" value={userAccount.usePeriodDate} />
                                </Th_title>
                            </tr>
                        )}
                        {isSystem(viewer) && (
                            <tr>
                                <Th_title title="사용기간(단위: 일)" colspan={3} className="fieldset-td" auth={userAccount.auth?.update}>
                                    <SimmsDatePicker
                                        onChange={(v) => dateOnChange(setUserAccount, "usePeriodDate", v, false)}
                                        data={userAccount.usePeriodDate}
                                    />
                                </Th_title>
                            </tr>
                        )}
                    </tbody>
                </UserAccountFormTable>
            </EditSection>

            {showSignModal && (
                <SignaturePadModal
                    user={{id: userId, name: userAccount.name}}
                    opener={setShowSignModal}
                    setter={setPendingSignPreview}
                    onSave={handleSignSave}
                />
            )}
        </div>
    );

    if (embedded) {
        return (
            <div className="user-account-manage-editor code-manage-scope">
                <div className="user-account-manage-editor__scroll">{formContent}</div>
            </div>
        );
    }

    return (
        <>
            {formContent}
            {!hideActions && (
                <div className="flex justify-end gap-2 px-2 mt-2">
                    <DetailSummaryButton muted onClick={() => (onCancel ? onCancel() : navigate("/settings/userAccount/list"))}>
                        취소
                    </DetailSummaryButton>
                    <DetailSummaryButton
                        onClick={() => {
                            if (window.confirm("저장 하시겠습니까?")) {
                                submit();
                            }
                        }}
                    >
                        저장
                    </DetailSummaryButton>
                </div>
            )}
        </>
    );
});

export default UserAccountEditForm;
